/**
 * Lightweight in-memory API response cache with stale-while-revalidate.
 * Pages get instant data from cache while a background refresh happens silently.
 */

const _cache = new Map();

const DEFAULT_TTL_MS = 60_000; // 1 minute default

/**
 * Get cached data for a key. Returns { data, isStale } or null if no cache.
 */
export function getCached(key) {
  const entry = _cache.get(key);
  if (!entry) return null;
  const age = Date.now() - entry.ts;
  return { data: entry.data, isStale: age > entry.ttl };
}

/**
 * Store data in cache.
 * @param {string} key - cache key (usually the API path)
 * @param {any} data - response data
 * @param {number} ttlMs - time-to-live in ms before considered stale
 */
export function setCache(key, data, ttlMs = DEFAULT_TTL_MS) {
  _cache.set(key, { data, ts: Date.now(), ttl: ttlMs });
}

/**
 * Invalidate a specific cache key or all keys matching a prefix.
 */
export function invalidateCache(keyOrPrefix) {
  if (_cache.has(keyOrPrefix)) {
    _cache.delete(keyOrPrefix);
  } else {
    for (const k of _cache.keys()) {
      if (k.startsWith(keyOrPrefix)) _cache.delete(k);
    }
  }
}

/**
 * Wrapper: returns cached data immediately if available, fetches in background.
 * @param {string} cacheKey
 * @param {Function} fetchFn - async function that returns data
 * @param {Function} onData - callback when fresh data arrives
 * @param {number} ttlMs
 * @returns {any|null} cached data (or null if cold start)
 */
export function cachedFetch(cacheKey, fetchFn, onData, ttlMs = DEFAULT_TTL_MS) {
  const cached = getCached(cacheKey);

  // Always trigger a background refresh
  fetchFn().then(data => {
    setCache(cacheKey, data, ttlMs);
    onData(data);
  }).catch(() => {});

  // Return stale data immediately if we have it
  return cached ? cached.data : null;
}
